import PreviewLayout from './PreviewLayout';
import PreviewCard from './PreviewCard';
import PreviewCardTable from './PreviewCardTable';
import styles from './SuggestionPreview.module.css';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getImplantPreview,
    getManufacturerPreview,
    getPolicyPreview,
    getTypePreview,
} from '../../../../services/apiService';
import { getFolderById } from '../../../../services/api/folderService';
import { folderCountsLabel } from '../../../group/folderCounts';
import SETTING_KEYS from '../../../../constants/setting-keys';
import Status from '../../../general-components/Status';

const SuggestionPreview = ({
    type,
    id,
}: {
    type: '' | 'implant' | 'type' | 'manufacturer' | 'folder' | 'policy';
    id: string;
}) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState<string | ReactNode>('');
    const [info, setInfo] = useState<{ [key: string]: any }>({});
    const [snapshot, setSnapshot] = useState<ReactNode>(null);

    const getPreview = async () => {
        if (type === 'implant') {
            const res = await getImplantPreview(id);
            setTitle(res[SETTING_KEYS.model]);
            setSubtitle(res[SETTING_KEYS.type]);
            setInfo({
                [SETTING_KEYS.manufacturer]: res[SETTING_KEYS.manufacturer],
                [t('search.suggestionPreview.info.updatedAt')]: new Date(
                    res.updatedAt
                ).toLocaleDateString(),
            });
            setSnapshot(
                <PreviewCard>
                    <p
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '30px',
                            gap: '10px',
                        }}
                    >
                        <Status status={res[SETTING_KEYS.mriStatus]} />
                        {res[SETTING_KEYS.mriStatus] === 3
                            ? t('search.suggestionPreview.mriStatus.mrSafe')
                            : ''}
                        {res[SETTING_KEYS.mriStatus] === 2
                            ? t(
                                  'search.suggestionPreview.mriStatus.mrConditional'
                              )
                            : ''}
                        {res[SETTING_KEYS.mriStatus] === 1
                            ? t('search.suggestionPreview.mriStatus.mrUnsafe')
                            : ''}
                        {res[SETTING_KEYS.mriStatus] === 0
                            ? t('search.suggestionPreview.mriStatus.mrUnknown')
                            : ''}
                    </p>
                    {res[SETTING_KEYS.fieldStrength] && (
                        <PreviewCardTable
                            title={SETTING_KEYS.fieldStrength}
                            value={res[SETTING_KEYS.fieldStrength]}
                        />
                    )}
                    {res[SETTING_KEYS.spatialGradient] && (
                        <PreviewCardTable
                            title={SETTING_KEYS.spatialGradient}
                            value={res[SETTING_KEYS.spatialGradient]}
                        />
                    )}
                </PreviewCard>
            );
        }
        if (type === 'manufacturer') {
            const res = await getManufacturerPreview(id);
            setTitle(res.name);
            setSubtitle(
                t('search.suggestionPreview.info.implantsInTotal', {
                    count: res.numberOfImplants,
                })
            );
            setInfo({});
            setSnapshot(
                <PreviewCard>
                    {res.implants.map((i: string) => (
                        <p style={{ display: 'flex' }}>{i}</p>
                    ))}
                </PreviewCard>
            );
        }
        if (type === 'type') {
            const res = await getTypePreview(id);
            setTitle(res.name);
            setSubtitle(
                t('search.suggestionPreview.info.implantsInTotal', {
                    count: res.numberOfImplants,
                })
            );
            setInfo({
                [SETTING_KEYS.bodyPlacement]:
                    res[SETTING_KEYS.bodyPlacement]?.join(', '),
                // Definition: res.definition,
            });
            setSnapshot(
                <>
                    {res.definition && (
                        <PreviewCard>{res.definition}</PreviewCard>
                    )}
                </>
            );
        }
        if (type === 'folder') {
            const res = await getFolderById(id);
            setTitle(res.title);
            setSubtitle(
                res.parent
                    ? t('folders.search.inParent', { parent: res.parent.title })
                    : ''
            );
            const childFolders = res.parent ? [] : res.subFolders;
            const sections = [
                {
                    heading: t('folders.detail.subFolders'),
                    items: childFolders.map((s) => ({
                        id: s._id,
                        name: s.title,
                    })),
                },
                {
                    heading: t('folders.detail.sections.implants'),
                    items: res.implants.map((i) => ({
                        id: i._id,
                        name: i.Model ?? '',
                    })),
                },
                {
                    heading: t('folders.detail.sections.policies'),
                    items: res.policies.map((p) => ({
                        id: p._id,
                        name: p.document_id?.title ?? '',
                    })),
                },
            ].filter((section) => section.items.length > 0);
            setInfo({});
            setSnapshot(
                <PreviewCard>
                    <p>
                        {folderCountsLabel(
                            {
                                subFolderCount: childFolders.length,
                                implantCount: res.implants.length,
                                scannerCount: res.scanners.length,
                                policyCount: res.policies.length,
                                fileCount: res.files.length,
                                linkCount: res.links.length,
                            },
                            t
                        )}
                    </p>
                    {sections.map(({ heading, items }) => (
                        <div key={heading} className="flex flex-col gap-1.5">
                            <p>{heading}</p>
                            {items.slice(0, 3).map((item) => (
                                <p
                                    key={item.id}
                                    style={{
                                        display: 'flex',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {item.name}
                                </p>
                            ))}
                            {items.length > 3 && (
                                <p>
                                    {t('folders.search.more', {
                                        count: items.length - 3,
                                    })}
                                </p>
                            )}
                        </div>
                    ))}
                </PreviewCard>
            );
        }
        if (type === 'policy') {
            const res = await getPolicyPreview(id);
            if (!res) return;
            setTitle(res.title);
            setSubtitle('');
            setInfo({
                Author: res.author,
                'Effective date': res.effectiveDate
                    ? new Date(res.effectiveDate).toLocaleDateString()
                    : '—',
                'Expiry date': res.expiryDate
                    ? new Date(res.expiryDate).toLocaleDateString()
                    : '—',
                ...(res.tags?.length > 0 && {
                    Tags: res.tags.join(', '),
                }),
            });
            setSnapshot(null);
        }
    };

    useEffect(() => {
        getPreview();
    }, [id]);

    return (
        <PreviewLayout title={title} subtitle={subtitle} info={info}>
            {snapshot}
        </PreviewLayout>
    );
};

export default SuggestionPreview;
